"use client";

import {
  Box,
  Button,
  type ButtonProps,
  Checkbox,
  Group,
  List,
  Modal,
  rem,
  Stack,
  Text,
  Transition,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import {
  IconAlertCircle,
  IconBrandStripe,
  IconGift,
  IconShield,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useAction } from "next-safe-action/hooks";
import { usePostHog } from "posthog-js/react";
import { useContext, useState } from "react";
import { AlertInfo } from "@/components/ui/Alert";
import { AppConfig } from "@/config";
import { SubscriptionPricesContext } from "@/helpers/contexts";
import { showErrorNotification } from "@/helpers/notifications";
import {
  ANALYTICS_EVENTS,
  type CheckoutStartedProps,
  type PaywallCtaClickedProps,
  type PaywallTrigger,
} from "@/lib/analytics/events";
import { centsToUsd, parseSubscriptionLookupKey } from "@/lib/analytics/plans";
import { getStripe } from "@/lib/stripe/client";
import { createCheckout } from "@/server/actions/store";

type CheckoutButtonProps = {
  priceLookupKey: string;
  fullWidth?: ButtonProps["fullWidth"];
  size?: ButtonProps["size"];
  /** Custom label for the button. Defaults to translated "Subscribe" */
  label?: string;
  returnUrl?: string;
  /**
   * Whether to show the "Start 7-day free trial" button.
   * Only shown for Premium plans when this is true.
   */
  showPremiumTrialButton?: boolean;
  /** Paywall this button is shown in, for `paywall_cta_clicked` / `checkout_started`. */
  trigger?: PaywallTrigger;
};

export function CheckoutButton({
  priceLookupKey,
  fullWidth = true,
  size = "lg",
  label,
  returnUrl,
  showPremiumTrialButton = false,
  trigger,
}: CheckoutButtonProps) {
  const t = useTranslations("app.dashboard.account.CheckoutButton");
  const [clientSecret, setClientSecret] = useState("");
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);

  const postHog = usePostHog();
  const buttonLabel = label ?? t("subscribe");
  const subscriptionPrices = useContext(SubscriptionPricesContext);
  const plan = parseSubscriptionLookupKey(priceLookupKey);

  // Only buttons shown in a paywall (given a trigger) report it.
  const trackCtaClick = (cta: PaywallCtaClickedProps["cta"]) => {
    if (!trigger) return;
    postHog.capture(ANALYTICS_EVENTS.paywallCtaClicked, {
      cta,
      ...plan,
      trigger,
    } satisfies PaywallCtaClickedProps);
  };

  const createCheckoutAction = useAction(createCheckout, {
    onSuccess: ({ data }) => {
      const price = subscriptionPrices?.find(
        (p) => p.price.lookupKey === priceLookupKey,
      )?.price;
      postHog.capture(ANALYTICS_EVENTS.checkoutStarted, {
        purchase_type: "subscription",
        ...plan,
        price_usd: centsToUsd(price?.unitAmount, price?.currency),
        trigger: trigger ?? null,
      } satisfies CheckoutStartedProps);

      if (!data) {
        return;
      }

      setClientSecret(data);
      open();
    },
  });

  const isPremium =
    priceLookupKey === AppConfig.stripe.premiumLookupKey ||
    priceLookupKey === AppConfig.stripe.premiumLookupKeyYearly;

  return (
    <>
      <Modal
        zIndex={1000}
        opened={opened}
        onClose={() => {
          close();
          setUnderstood(false);
        }}
        radius={"lg"}
        keepMounted={false}
        fullScreen
        // miw={rem(400)}
        // size="80%"
        // styles={{
        //   header: { height: "65px" },
        //   body: { height: "calc(100vh - 65px)" },
        // }}
        title={
          <Group gap={5}>
            <IconBrandStripe />
            <Text size="lg">{t("modalTitle")}</Text>
          </Group>
        }
        transitionProps={{ transition: "slide-up", duration: 300 }}
        // overlayProps={{
        //   backgroundOpacity: 0.55,
        //   blur: 3,
        // }}
      >
        <EmbeddedCheckoutProvider
          stripe={getStripe()}
          options={{
            clientSecret: clientSecret,
          }}
        >
          <Stack>
            <Transition
              mounted={isFreeTrial}
              transition="slide-down"
              duration={300}
              enterDelay={5000}
              keepMounted={false}
            >
              {(styles) => (
                <AlertInfo
                  mx="auto"
                  w={{ base: "100%", lg: "80%" }}
                  maw={rem(888)}
                  style={styles}
                >
                  <Stack gap="md">
                    <Text fw={600} size="md">
                      {t("trialTerms.title")}
                    </Text>
                    <List withPadding spacing="xs">
                      <List.Item
                        icon={<IconGift size={20} style={{ marginTop: 4 }} />}
                      >
                        {t.rich("trialTerms.duration", {
                          days: AppConfig.trial.periodDays,
                          credits: AppConfig.trial.creditsIncluded,
                          daysText: (chunks) => (
                            <Text component="span" fw={700} td="underline">
                              {chunks}
                            </Text>
                          ),
                          creditsText: (chunks) => (
                            <Text component="span" fw={700} td="underline">
                              {chunks}
                            </Text>
                          ),
                        })}
                      </List.Item>
                      <List.Item
                        icon={<IconShield size={20} style={{ marginTop: 4 }} />}
                      >
                        {t.rich("trialTerms.watermark", {
                          watermark: (chunks) => (
                            <Text component="span" fw={700} td="underline">
                              {chunks}
                            </Text>
                          ),
                        })}
                      </List.Item>
                      <List.Item
                        icon={
                          <IconAlertCircle size={20} style={{ marginTop: 4 }} />
                        }
                      >
                        <Text component="span">
                          {t.rich("trialTerms.autoCharge", {
                            autoCharge: (chunks) => (
                              <Text component="span" fw={700} td="underline">
                                {chunks}
                              </Text>
                            ),
                          })}
                        </Text>
                      </List.Item>
                    </List>
                    <Checkbox
                      size="lg"
                      label={t("trialTerms.confirmCheckbox")}
                      checked={understood}
                      onChange={(event) =>
                        setUnderstood(event.currentTarget.checked)
                      }
                    />
                  </Stack>
                </AlertInfo>
              )}
            </Transition>

            <Box
              style={{
                pointerEvents: isFreeTrial && !understood ? "none" : "auto",
                opacity: isFreeTrial && !understood ? 0.3 : 1,
                transition: "opacity 0.3s ease",
              }}
            >
              <EmbeddedCheckout />
            </Box>
          </Stack>
        </EmbeddedCheckoutProvider>
      </Modal>

      <Stack>
        {/* Free trial button - only shown for Premium plans when enabled */}
        {isPremium && showPremiumTrialButton && (
          <Button
            fullWidth={fullWidth}
            variant="gradient"
            gradient={{ from: "purple.5", to: "yellow.8" }}
            loading={createCheckoutAction.isExecuting}
            size={size}
            onClick={async () => {
              postHog.capture("start_free_trial_clicked");
              trackCtaClick("start_trial");
              setIsFreeTrial(true);

              const res = await createCheckoutAction.executeAsync({
                priceLookupKey,
                returnUrl,
                freeTrialRequested: true,
              });

              if (res?.serverError) {
                showErrorNotification({
                  title: t("errors.couldNotStartTrial"),
                  message: res.serverError ?? t("errors.trialFallback"),
                });
              }
            }}
          >
            {t("startFreeTrial", { days: AppConfig.trial.periodDays })}
          </Button>
        )}

        <Button
          fullWidth={fullWidth}
          variant="gradient"
          gradient={{ from: "purple.8", to: "purple.5" }}
          loading={createCheckoutAction.isExecuting}
          size={size}
          onClick={async () => {
            trackCtaClick("upgrade");
            setIsFreeTrial(false);

            const res = await createCheckoutAction.executeAsync({
              priceLookupKey,
              returnUrl,
            });

            if (res?.serverError) {
              showErrorNotification({
                title: t("errors.couldNotCreateCheckout"),
                message: res.serverError ?? t("errors.checkoutFallback"),
              });
            }
          }}
        >
          {buttonLabel}
        </Button>
      </Stack>
    </>
  );
}
